import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from 'components/ui/icon';
import 'components/ui/tooltip.scss';

const PLACEMENTS = ['top', 'bottom', 'left', 'right'];

const resolveTarget = (target) => {
  if (!target) return null;
  if (typeof target === 'string') return document.getElementById(target);
  if (target.current) return target.current;
  if (target instanceof Element) return target;
  return null;
};

const computePosition = (triggerRect, tipRect, placement, offset) => {
  const margin = offset;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top;
  let left;

  switch (placement) {
    case 'bottom':
      top = triggerRect.bottom + margin;
      left = triggerRect.left + (triggerRect.width - tipRect.width) / 2;
      break;
    case 'left':
      top = triggerRect.top + (triggerRect.height - tipRect.height) / 2;
      left = triggerRect.left - tipRect.width - margin;
      break;
    case 'right':
      top = triggerRect.top + (triggerRect.height - tipRect.height) / 2;
      left = triggerRect.right + margin;
      break;
    default:
      top = triggerRect.top - tipRect.height - margin;
      left = triggerRect.left + (triggerRect.width - tipRect.width) / 2;
      break;
  }

  top = Math.max(margin, Math.min(top, vh - tipRect.height - margin));
  left = Math.max(margin, Math.min(left, vw - tipRect.width - margin));
  return { top, left };
};

// Targeted mode: <Tooltip target={id|ref}>content</Tooltip>
// Wrapped mode: <Tooltip content="...">{children}</Tooltip>
const Tooltip = ({
  target,
  content,
  title,
  children,
  placement = 'top',
  delay = 100,
  offset = 8,
  className,
  variant = 'dark',
  showArrow = true,
  isOpen,
}) => {
  const isControlled = isOpen !== undefined;
  const [internalVisible, setInternalVisible] = useState(false);
  const visible = isControlled ? isOpen : internalVisible;
  const setVisible = isControlled ? () => {} : setInternalVisible;
  const [position, setPosition] = useState(null);
  const triggerWrapperRef = useRef(null);
  const tipRef = useRef(null);
  const targetElRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  // In targeted mode, fallback to children for content if neither content nor title is provided.
  const tipBody = content !== undefined ? content : title !== undefined ? title : target ? children : undefined;

  const clearTimers = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    showTimerRef.current = null;
    hideTimerRef.current = null;
  };

  const show = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (delay > 0) {
      showTimerRef.current = setTimeout(() => setVisible(true), delay);
    } else {
      setVisible(true);
    }
  }, [delay]);

  const hide = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setVisible(false);
  }, []);

  // Targeted mode: bind events on the resolved target element
  useEffect(() => {
    if (!target) return undefined;
    const el = resolveTarget(target);
    targetElRef.current = el;
    if (!el) return undefined;
    el.addEventListener('mouseenter', show);
    el.addEventListener('mouseleave', hide);
    el.addEventListener('focus', show);
    el.addEventListener('blur', hide);
    return () => {
      el.removeEventListener('mouseenter', show);
      el.removeEventListener('mouseleave', hide);
      el.removeEventListener('focus', show);
      el.removeEventListener('blur', hide);
    };
  }, [target, show, hide]);

  // Position the tooltip
  const reposition = useCallback(() => {
    let trigger;
    if (target) {
      trigger = targetElRef.current || resolveTarget(target);
      if (trigger) targetElRef.current = trigger;
    } else {
      trigger = triggerWrapperRef.current;
    }
    if (!trigger || !tipRef.current) return;
    const triggerRect = trigger.getBoundingClientRect();
    const tipRect = tipRef.current.getBoundingClientRect();
    setPosition(computePosition(triggerRect, tipRect, placement, offset));
  }, [target, placement, offset]);

  useLayoutEffect(() => {
    if (!visible) {
      setPosition(null);
      return undefined;
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [visible, reposition]);

  useEffect(() => clearTimers, []);

  // Wrapped mode renders an inline span around children + the floating tip
  // Targeted mode renders only the floating tip (target element is external)
  const renderTip = () =>
    visible && tipBody !== undefined && tipBody !== null && tipBody !== ''
      ? ReactDOM.createPortal(
          <span
            ref={tipRef}
            className={classNames('ui-tooltip', `ui-tooltip--${variant}`, `ui-tooltip--${placement}`, className)}
            style={{
              top: position ? position.top : 0,
              left: position ? position.left : 0,
            }}
            role="tooltip"
          >
            {showArrow && <span className={`ui-tooltip-arrow ui-tooltip-arrow--${placement}`} />}
            {tipBody}
          </span>,
          document.body,
        )
      : null;

  if (target) {
    return renderTip();
  }

  return (
    <>
      <span
        ref={triggerWrapperRef}
        className="ui-tooltip-trigger"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children || <Icon name="information-circle" className={className} />}
      </span>
      {renderTip()}
    </>
  );
};

Tooltip.propTypes = {
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ current: PropTypes.any }), PropTypes.instanceOf(Element)]),
  content: PropTypes.node,
  title: PropTypes.node,
  children: PropTypes.node,
  placement: PropTypes.oneOf(PLACEMENTS),
  delay: PropTypes.number,
  offset: PropTypes.number,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['dark', 'light']),
  showArrow: PropTypes.bool,
  isOpen: PropTypes.bool,
};

export default Tooltip;
